/**
 * EmailListe - Viser e-poster knyttet til en sak
 * Inkluderer AI-klassifisering og automatisk TODO-opprettelse
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './EmailListe.css';

export default function EmailListe({ sakId }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [filter, setFilter] = useState('alle'); // 'alle', 'inn', 'ut'

  useEffect(() => {
    if (sakId) {
      lastEmails();
    }
  }, [sakId]);

  const lastEmails = async () => {
    try {
      setLoading(true);
      const data = await crmApi.request(`/api/saker/${sakId}/emails`);
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Feil ved lasting av e-poster:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtrerteEmails = emails.filter(email => {
    if (filter === 'inn') return email.retning === 'inn';
    if (filter === 'ut') return email.retning === 'ut';
    return true;
  });

  const formaterDato = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const dager = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (dager === 0) {
      return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    } else if (dager === 1) {
      return 'I går';
    } else if (dager < 7) {
      return `${dager} dager siden`;
    } else {
      return date.toLocaleDateString('nb-NO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  };

  const getRetningIkon = (retning) => {
    return retning === 'inn' ? '📥' : '📤';
  };

  const getAIKonfidenseFarge = (confidence) => {
    if (!confidence) return '#999';
    if (confidence >= 0.9) return '#22c55e'; // Grønn
    if (confidence >= 0.7) return '#eab308'; // Gul
    return '#ef4444'; // Rød
  };

  if (loading) {
    return (
      <div className="email-liste-container">
        <div className="email-loading">📧 Laster e-poster...</div>
      </div>
    );
  }

  return (
    <div className="email-liste-container">
      <div className="email-header">
        <div className="email-header-info">
          <h3>📧 E-poster ({filtrerteEmails.length})</h3>
          <p>AI-klassifisert og automatisk knyttet til saken</p>
        </div>

        <div className="email-filter">
          <button
            className={filter === 'alle' ? 'active' : ''}
            onClick={() => setFilter('alle')}
          >
            Alle ({emails.length})
          </button>
          <button
            className={filter === 'inn' ? 'active' : ''}
            onClick={() => setFilter('inn')}
          >
            📥 Innkommende ({emails.filter(e => e.retning === 'inn').length})
          </button>
          <button
            className={filter === 'ut' ? 'active' : ''}
            onClick={() => setFilter('ut')}
          >
            📤 Utgående ({emails.filter(e => e.retning === 'ut').length})
          </button>
        </div>
      </div>

      {filtrerteEmails.length === 0 ? (
        <div className="email-empty-state">
          <p>📭 Ingen e-poster funnet</p>
          <small>E-poster vil automatisk dukke opp her når de mottas i Outlook</small>
        </div>
      ) : (
        <div className="email-liste">
          {filtrerteEmails.map((email) => (
            <div
              key={email.id}
              className={`email-item ${selectedEmail?.id === email.id ? 'selected' : ''}`}
              onClick={() => setSelectedEmail(email)}
            >
              <div className="email-item-header">
                <span className="email-retning">{getRetningIkon(email.retning)}</span>
                <span className="email-from">{email.from_address}</span>
                <span className="email-dato">{formaterDato(email.received_at)}</span>
              </div>

              <div className="email-item-subject">
                {email.subject || '(Ingen emne)'}
              </div>

              <div className="email-item-footer">
                {email.ai_classified && (
                  <span
                    className="email-ai-badge"
                    style={{
                      backgroundColor: getAIKonfidenseFarge(email.classification_confidence)
                    }}
                    title={`AI-klassifisert med ${Math.round(email.classification_confidence * 100)}% konfidans`}
                  >
                    🤖 AI {Math.round(email.classification_confidence * 100)}%
                  </span>
                )}

                {email.oppgave_created && (
                  <span className="email-todo-badge" title="Oppgave opprettet">
                    ✅ TODO
                  </span>
                )}

                {email.sharepoint_url && (
                  <span className="email-sharepoint-badge" title="Lagret i SharePoint">
                    📁 SharePoint
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* E-post detaljer modal */}
      {selectedEmail && (
        <div className="modal-overlay" onClick={() => setSelectedEmail(null)}>
          <div className="modal-content email-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-detail-header">
              <div>
                <h2>{getRetningIkon(selectedEmail.retning)} {selectedEmail.subject || '(Ingen emne)'}</h2>
                <p className="email-detail-meta">
                  <strong>Fra:</strong> {selectedEmail.from_address}<br/>
                  <strong>Til:</strong> {selectedEmail.to_addresses.join(', ')}<br/>
                  <strong>Dato:</strong> {new Date(selectedEmail.received_at).toLocaleString('nb-NO')}
                </p>
              </div>
              <button className="btn-close" onClick={() => setSelectedEmail(null)}>✕</button>
            </div>

            {selectedEmail.ai_classified && (
              <div className="email-ai-info">
                <strong>🤖 AI-klassifisering</strong>
                <p>
                  Konfidans: <span style={{ color: getAIKonfidenseFarge(selectedEmail.classification_confidence) }}>
                    {Math.round(selectedEmail.classification_confidence * 100)}%
                  </span>
                </p>
              </div>
            )}

            <div className="email-detail-body">
              <pre>{selectedEmail.body}</pre>
            </div>

            {selectedEmail.sharepoint_url && (
              <div className="email-detail-actions">
                <a
                  href={selectedEmail.sharepoint_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📁 Åpne i SharePoint
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
